import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import spin from 'assets/img/spin.png';
import ruby from 'assets/img/ruby-icon.png';
import giftPrize from 'assets/img/wheel-prize-gift.png';
import oksPrize from 'assets/img/wheel-prize-ok.png';
import framePrize from 'assets/img/wheel-prize-frame.png';
import promoPrize from 'assets/img/wheel-prize-promo.png';
import surprizePrize from 'assets/img/wheel-prize-surprize.png';
import toyPrize from 'assets/img/wheel-prize-toy.png';
import './Wheel.styl';
import { WheelPageFooter } from './WheelPageFooter';
import { useStoreContext } from 'store/app';
import { useLocation, useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import { LotteryRewardTypes, ToastTypes } from 'src/types';
import { toastVisibleDuration } from 'components/core/Toast';
import { routes } from 'utils/router';
import { useTranslationsContext } from 'store/translations';
import { useResize } from 'src/hooks';

const LAPS_COUNT = 4;
const SECTOR_DEG = 45;
const WHEEL_DURATION = 12;
const WHEEL_INIT_DEG = 202;

interface WheelProps {
    onShowPrize: () => void;
}

export const Wheel = observer(({ onShowPrize }: WheelProps) => {
    const { translate } = useTranslationsContext();
    const { isMob, rollRevolverReward, rollRevolverCollection, questsConfig, revolverPrize, showToast, logOperation } =
        useStoreContext();
    const {
        state: { collectionId, missionId }
    } = useLocation() as { state: { collectionId: string; missionId: string } };
    const navigate = useNavigate();
    const [isDisabledSpin, setIsDisabledSpin] = useState(false);
    const sectorsRef = useRef<HTMLDivElement>(null);
    const [footerMarginTop, setFooterMarginTop] = useState(0);
    const { width, height } = useResize();

    const isBigWheel = isMob && width > 630;
    const wheelWidth = isBigWheel ? 900 : 628;

    const [transform, setTransform] = useState(`rotate(${WHEEL_INIT_DEG}deg)`);

    const sectors = [
        { key: LotteryRewardTypes.FRAME, name: translate('wheel.sector.frame'), img: framePrize },
        {
            key: LotteryRewardTypes.RUBY_50,
            name: translate('wheel.sector.rubies', { count: 50, countPlural: 50 }),
            img: ruby
        },
        { key: LotteryRewardTypes.GIFT, name: translate('wheel.sector.gift'), img: giftPrize },
        { key: LotteryRewardTypes.SURPRIZE, name: translate('wheel.sector.surprize'), img: surprizePrize },
        {
            key: LotteryRewardTypes.TOY,
            name: translate('wheel.sector.toy'),
            img: toyPrize
        },
        { key: LotteryRewardTypes.OKS, name: translate('wheel.sector.oks'), img: oksPrize },
        {
            key: LotteryRewardTypes.RUBY_100,
            name: translate('wheel.sector.rubies', { count: 100, countPlural: 100 }),
            img: ruby
        },
        { key: LotteryRewardTypes.CODE, name: translate('wheel.sector.promo'), img: promoPrize }
    ];

    const onSpinClick = () => {
        collectionId
            ? logOperation('fortune', 'open_collection', collectionId)
            : logOperation('fortune', 'open_mission', missionId);
        setIsDisabledSpin(true);

        if (missionId) {
            const reward = questsConfig?.rewards.find((x) => x.rolled === false && x.mission_id === missionId);

            reward &&
                rollRevolverReward(reward).catch((err) => {
                    console.warn(err);
                    showToast(translate('wheel.roll.revolver.error'), ToastTypes.ERROR);
                    setTimeout(() => {
                        navigate(routes.main);
                    }, toastVisibleDuration);
                });
        } else {
            rollRevolverCollection(+collectionId);
        }
    };

    useEffect(() => {
        if (revolverPrize) {
            const prizeIndex = sectors.findIndex((item) => item.key === revolverPrize.lottery_reward_type);
            const deg = 360 * LAPS_COUNT + SECTOR_DEG * (sectors.length - prizeIndex) + WHEEL_INIT_DEG;

            setTransform(`rotate(${deg}deg)`);

            setTimeout(() => {
                onShowPrize();
            }, WHEEL_DURATION * 1000 + 500);
        }
    }, [revolverPrize, sectors]);

    useLayoutEffect(() => {
        if (sectorsRef?.current) {
            const { top, height: sectorsHeight } = sectorsRef.current.getBoundingClientRect();
            setFooterMarginTop(sectorsHeight + top + (isBigWheel ? 50 : isMob ? 0 : 60));
        }
    }, [isMob, isBigWheel]);

    return (
        <div className="wheel-container">
            <div className="wheel" style={{ width: wheelWidth }}>
                <img
                    className="wheel-spin"
                    style={{ bottom: isBigWheel ? -22 : undefined, width: isBigWheel ? 50 : undefined }}
                    src={spin}
                />
                <div className="wheel-sectors" style={{ width: wheelWidth }} ref={sectorsRef} />

                <div
                    className="wheel-core"
                    style={{
                        transform,
                        transition: `transform ${WHEEL_DURATION}s cubic-bezier(.14,.04,.29,1.08)`,
                        width: wheelWidth
                    }}
                >
                    {sectors.map(({ key, img, name }, index) => (
                        <div
                            className="wheel-sector"
                            style={{
                                transform: `rotate(calc(45deg * ${index + 1}))`
                            }}
                            key={key}
                        >
                            <div
                                className="wheel-sector-content-container"
                                style={{ left: isBigWheel ? '51%' : undefined }}
                            >
                                <div className="wheel-sector-content">
                                    <img className="wheel-sector-content-img" src={img} />
                                    <span className="wheel-sector-content-name">{name}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div
                className="wheel-footer"
                style={{ marginTop: footerMarginTop, bottom: isMob && height <= 620 ? '10%' : undefined }}
            >
                <WheelPageFooter
                    onClick={onSpinClick}
                    btnDisabled={isDisabledSpin}
                    title={translate('wheel.title')}
                    subtitle={translate('wheel.subtitle')}
                    btnText={translate(isDisabledSpin ? 'wheel.button.playing' : 'wheel.button.play')}
                />
            </div>
        </div>
    );
});
